import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FolderOpen, Loader2, MoreHorizontal } from 'lucide-react';
import { useMediaQuery } from '@/hooks/ui/useMediaQuery';
import { cn, Sheet } from '@ai-tutor/ui';
import { useShellSlots } from '@/contexts/ShellSlotsContext';
import { InlineMarkdown } from '@/components/core/markdown/markdown';
import { stripMath } from '@/lib/markdown/previewText';
import {
  LessonOptionsMenu,
  LessonProgressContent,
} from '../lesson/LessonOptionsMenu';

/**
 * What the sources button shows at a glance. `busy`: something is still
 * processing. `attention`: something failed or is waiting for the user to
 * pick chapters - worth opening the sheet for.
 */
export type SourcesBadgeState = 'idle' | 'busy' | 'attention';

export interface LessonTopBarProps {
  lessonId?: string;
  /** The lesson's title; a neutral placeholder renders until it loads. */
  title?: string;
  sourceCount: number;
  sourcesState: SourcesBadgeState;
  onOpenSources: () => void;
  /**
   * Render the shell's navbar accessory (the running focus timer). On a
   * phone this bar REPLACES the app navbar (`AppLayout hideNavbarOnPhone`),
   * so the timer pill has to come with it or it vanishes mid-block.
   */
  showShellAccessory?: boolean;
}

/**
 * The lesson's own top bar on phones and tablets: back to the library, the
 * lesson's real title, the sources button and the Progress menu.
 *
 * It replaces a bar that only said "Lesson" above a toggle that swapped the
 * whole lesson out for a sources + chat split. Sources now open in a sheet
 * from here, the chat from the page's Ask button, and the lesson itself never
 * leaves the screen.
 */
export function LessonTopBar({
  lessonId,
  title,
  sourceCount,
  sourcesState,
  onOpenSources,
  showShellAccessory = false,
}: LessonTopBarProps) {
  const navigate = useNavigate();
  const { navbarAccessory } = useShellSlots();

  return (
    // `relative`: the page lattice is a positioned layer, and it paints over
    // the background of anything that is not - the bar showed grid lines.
    // `gap-2`: the 36px buttons' 44px tap areas overhang by 4px a side, so
    // anything tighter let one button's area cover its neighbour's.
    <div className='relative flex h-14 flex-shrink-0 items-center gap-2 border-b border-border bg-card px-2'>
      <button
        type='button'
        onClick={() => navigate('/my-learning')}
        aria-label='Back to My Learning'
        className='tap-target rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
      >
        <ArrowLeft className='h-5 w-5' />
      </button>

      {/* Text, not a heading: the lesson's own content opens with its title
          as a heading, and the panels below start at h3 - an h1 here broke
          the heading order on every tab (axe heading-order). */}
      <p
        data-testid='lesson-top-bar-title'
        className='min-w-0 flex-1 truncate px-1 text-base font-semibold text-foreground'
        title={title && stripMath(title)}
      >
        {title ? <InlineMarkdown>{title}</InlineMarkdown> : 'Lesson'}
      </p>

      {showShellAccessory && navbarAccessory}

      <SourcesButton
        count={sourceCount}
        state={sourcesState}
        onClick={onOpenSources}
      />
      <OptionsButton lessonId={lessonId} />
    </div>
  );
}

/**
 * The sources button: a count, a spinner while something processes, a dot
 * when something failed or awaits chapters. The name carries the same state.
 */
function SourcesButton({
  count,
  state,
  onClick,
}: {
  count: number;
  state: SourcesBadgeState;
  onClick: () => void;
}) {
  const label = [
    count === 1 ? 'Sources, 1 source' : `Sources, ${count} sources`,
    state === 'busy' && 'still processing',
    state === 'attention' && 'needs attention',
  ]
    .filter(Boolean)
    .join(', ');
  const Icon = state === 'busy' ? Loader2 : FolderOpen;
  return (
    <button
      type='button'
      onClick={onClick}
      aria-label={label}
      className='tap-target relative flex items-center gap-1.5 rounded-md px-2 py-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground'
    >
      <Icon
        className={cn(
          'h-5 w-5',
          state === 'busy' && 'animate-spin motion-reduce:animate-none'
        )}
        aria-hidden='true'
      />
      {count > 0 && (
        <span className='font-mono text-xs tabular-nums'>
          {count > 9 ? '9+' : count}
        </span>
      )}
      {state === 'attention' && (
        <span
          data-testid='sources-attention-dot'
          className='absolute right-1 top-1.5 h-2 w-2 rounded-full bg-warning ring-2 ring-card'
        />
      )}
    </button>
  );
}

/** ⋯ - the lesson's Progress menu, moved here from the desktop tab row. */
function OptionsButton({ lessonId }: { lessonId?: string }) {
  const [open, setOpen] = useState(false);
  const phone = useMediaQuery('(max-width: 639.98px)');
  const triggerRef = useRef<HTMLButtonElement>(null);
  return (
    <div className='relative'>
      <button
        ref={triggerRef}
        type='button'
        onClick={() => setOpen(o => !o)}
        aria-label='Progress and options'
        aria-expanded={open}
        className={cn(
          'tap-target rounded-md p-2 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground',
          open && 'bg-muted text-foreground'
        )}
      >
        <MoreHorizontal className='h-5 w-5' />
      </button>
      {phone ? (
        // A bottom sheet on a phone: the popover's screen-wide click shield
        // left nothing else tappable while it was open, and a 288px card
        // pinned under the corner is not where a thumb reads from.
        <Sheet
          isOpen={open}
          onClose={() => setOpen(false)}
          ariaLabel='Lesson progress'
          showHeader={false}
          size='auto'
        >
          <div className='pb-[env(safe-area-inset-bottom)]'>
            <LessonProgressContent lessonId={lessonId} />
          </div>
        </Sheet>
      ) : (
        open && (
          <LessonOptionsMenu
            lessonId={lessonId}
            onClose={() => setOpen(false)}
            triggerRef={triggerRef}
          />
        )
      )}
    </div>
  );
}
